import { useState } from 'react'
import { ScrollView, Text, View } from 'react-native'
import { useQuery } from '@apollo/client'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { GET_ASSET_REQUEST_BY_INTERNAL_KEY, GET_WALLET_EVENT_BY_NUMBER } from '@/constants/graphql/queries'
import { formatMarketNumber, formatTokenAmount, marketPresentation } from '@/config/marketPresentation'
import { WalletEvent, WalletEventKind } from '@/types/wallet'
import { convertToDate, formatDate } from '@/utils/formatter'
import { getExchangeRate, toCurrency } from '@/utils/numbers'
import DescriptionListItem from '@/components/DescriptionListItem'
import { Stack } from '@/components/layout'
import LoadingScreen from '@/components/LoadingScreen'
import NavigationBar from '@/components/NavigationBar'
import NotFoundScreen from '@/components/NotFoundScreen'
import SectionHeader from '@/components/SectionHeader'

export default function RecordDetailsScreen() {
  const { t } = useTranslation()
  const { bottom } = useSafeAreaInsets()
  const { id } = useLocalSearchParams<Record<string, string>>()
  const [walletEvent, setWalletEvent] = useState<WalletEvent>()
  const [eventKind, setEventKind] = useState<string>('')

  // TODO: Handle loading
  const { loading } = useQuery(GET_WALLET_EVENT_BY_NUMBER, {
    variables: {
      number: id
    },
    onCompleted: (data) => {
      // console.log('data:', data)

      // console.log('kind', data.getWalletEventByNumber.kind)
      setWalletEvent(data.getWalletEventByNumber)
      setEventKind(t(`walletEvent:${data.getWalletEventByNumber.kind}`))
    },
    onError: (error) => console.log(error)
  })

  let Component = (_: { walletEvent: WalletEvent }) => <></>

  switch (walletEvent?.kind) {
    case WalletEventKind[WalletEventKind.Credited]:
    case WalletEventKind[WalletEventKind.BonusActivityRewarded]:
    case WalletEventKind[WalletEventKind.DebitCompleted]:
      Component = Credited
      break
    case WalletEventKind[WalletEventKind.ExchangedIn]:
      Component = ExchangedIn
      break
    case WalletEventKind[WalletEventKind.Transferred]:
    case WalletEventKind[WalletEventKind.TransferredWithBonus]:
      Component = Transferred
      break
    case WalletEventKind[WalletEventKind.Received]:
      Component = Received
      break
    default:
      break
  }

  if (loading) {
    return <LoadingScreen />
  }

  if (!walletEvent) {
    return <NotFoundScreen />
  }

  return (
    <>
      <Stack.Screen options={{ header: () => <NavigationBar /> }} />
      <ScrollView className={'bg-background-secondary'} contentContainerStyle={{ paddingBottom: bottom }}>
        <View className={'h-[52px] border-b border-border-10 bg-background-primary px-4 pt-2'}>
          <Text className={'text-2xl font-semibold text-text-primary'}>{eventKind}</Text>
        </View>
        {walletEvent && <Component walletEvent={walletEvent} />}
      </ScrollView>
    </>
  )
}

function Credited({ walletEvent }: { walletEvent: WalletEvent }) {
  const { t } = useTranslation()
  const jbpSystemOrderId = getJbpSystemOrderId(walletEvent)
  let label = ''
  switch (walletEvent.kind) {
    case WalletEventKind[WalletEventKind.Credited]:
      label = t('wallet:increasedQuantity')
      break
    case WalletEventKind[WalletEventKind.DebitCompleted]:
      label = t('wallet:decreasedQuantity')
      break
    case WalletEventKind[WalletEventKind.BonusActivityRewarded]:
      label = t('wallet:rewardQuantity')
      break
  }
  return (
    <>
      <SectionHeader>{t('orders:orderInformation')}</SectionHeader>
      {jbpSystemOrderId && (
        <DescriptionListItem label={t('wallet:jbpSystemOrderNumber')} canCopy={true} hasBorderTop={true}>
          {jbpSystemOrderId}
        </DescriptionListItem>
      )}
      <DescriptionListItem
        label={t(jbpSystemOrderId ? 'wallet:walletTransactionNumber' : 'orders:systemOrderNumber')}
        canCopy={true}
        hasBorderTop={!jbpSystemOrderId}
      >
        {walletEvent.internalKey}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:createdAt')}>
        {convertToDate(new Date(walletEvent.when).valueOf())}
      </DescriptionListItem>
      <DescriptionListItem label={label} hasBorderBottom={true}>
        {formatTokenAmount(walletEvent.amount.value)}
      </DescriptionListItem>
    </>
  )
}

function getJbpSystemOrderId(walletEvent: WalletEvent) {
  const metadata = walletEvent.metadataExt
  const companyOrderNum = metadata?.provider === 'JBP' ? metadata.company_order_num : undefined
  return typeof companyOrderNum === 'string' && companyOrderNum.length > 0 ? companyOrderNum : undefined
}

function ExchangedIn({ walletEvent }: { walletEvent: WalletEvent }) {
  const { t } = useTranslation()
  const { data: assetRequestData } = useQuery(GET_ASSET_REQUEST_BY_INTERNAL_KEY, {
    variables: {
      internalKey: walletEvent.internalKey
    }
  })

  return (
    <>
      <SectionHeader>{t('orders:orderInformation')}</SectionHeader>
      <DescriptionListItem label={t('orders:systemOrderNumber')} canCopy={true} hasBorderTop={true}>
        {walletEvent.internalKey}
      </DescriptionListItem>
      <DescriptionListItem label={t('wallet:transactionId')} canCopy={true}>
        {assetRequestData ? assetRequestData.getAssetRequestByInternalKey.txHash : '−'}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:createdAt')}>{formatDate(walletEvent.when)}</DescriptionListItem>
      <DescriptionListItem label={t('wallet:exchangeRate')}>
        {assetRequestData
          ? t('wallet:exchangeRateFromUsdt', {
              amount: formatMarketNumber(
                getExchangeRate(walletEvent.amount.value, assetRequestData.getAssetRequestByInternalKey.amount.value).toString(),
                'YBB'
              ),
              token: marketPresentation.platformToken.displayName
            })
          : '−'}
      </DescriptionListItem>
      <DescriptionListItem label={t('wallet:transferInAmount')}>
        {assetRequestData ? `${toCurrency(assetRequestData.getAssetRequestByInternalKey.amount.value, 6)} USDT` : '−'}
      </DescriptionListItem>
      <DescriptionListItem label={t('wallet:convertedAmount')} hasBorderBottom={true}>
        {formatTokenAmount(walletEvent.amount.value)}
      </DescriptionListItem>
    </>
  )
}

function Transferred({ walletEvent }: { walletEvent: WalletEvent }) {
  const { t } = useTranslation()
  return (
    <>
      <SectionHeader>{t('orders:orderInformation')}</SectionHeader>
      <DescriptionListItem label={t('orders:systemOrderNumber')} canCopy={true} hasBorderTop={true}>
        {walletEvent.internalKey}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:createdAt')}>
        {convertToDate(new Date(walletEvent.metadataExt?.createdAt).valueOf())}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:completedAt')}>
        {convertToDate(new Date(walletEvent.when).valueOf())}
      </DescriptionListItem>
      <DescriptionListItem label={t('wallet:paymentQuantity')} hasBorderBottom={true}>
        {formatTokenAmount(walletEvent?.amount.value ?? '0')}
      </DescriptionListItem>
    </>
  )
}

function Received({ walletEvent }: { walletEvent: WalletEvent }) {
  const { t } = useTranslation()
  return (
    <>
      <SectionHeader>{t('orders:orderInformation')}</SectionHeader>
      <DescriptionListItem label={t('orders:systemOrderNumber')} canCopy={true} hasBorderTop={true}>
        {walletEvent.internalKey}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:createdAt')}>{formatDate(walletEvent.when)}</DescriptionListItem>
      <DescriptionListItem label={t('wallet:receivedQuantity')} hasBorderBottom={true}>
        {formatTokenAmount(walletEvent.amount.value)}
      </DescriptionListItem>
    </>
  )
}
